import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '../../';
import parseStatusCode from '../../errors/parseStatusCode';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { PartnerContact, PartnerContactCreate, PartnerContactUpdate } from '@shipium-corp/shipium-ts-types';

export interface ContactsModel {
  contactsMap: Record<string, PartnerContact>;
  setContacts: Action<ContactsModel, PartnerContact[]>;
  setContact: Action<ContactsModel, PartnerContact>;
  fetchContactsLoading: boolean;
  setFetchContactsLoading: Action<ContactsModel, boolean>;
  fetchContacts: Thunk<ContactsModel, void, Injections, StoreModel, Promise<void>>;
  fetchContactsFailure: Action<ContactsModel, ApplicationErrorOptions>;
  createContact: Thunk<ContactsModel, PartnerContactCreate, Injections, StoreModel, Promise<PartnerContact>>;
  updateContact: Thunk<
    ContactsModel,
    { contactId: string; payload: PartnerContactUpdate },
    Injections,
    StoreModel,
    Promise<PartnerContact>
  >;
  createContactFailure: Action<ContactsModel, ApplicationErrorOptions>;
  updateContactFailure: Action<ContactsModel, ApplicationErrorOptions>;
}

export const contactsModel: ContactsModel = {
  contactsMap: {},
  setContacts: action((state, contacts) => {
    const contactMap = contacts.reduce<Record<string, PartnerContact>>((acc, curr) => {
      acc[curr.contactId!] = curr;
      return acc;
    }, {});
    state.contactsMap = contactMap;
  }),
  setContact: action((state, contact) => {
    state.contactsMap[contact.contactId!] = contact;
  }),
  fetchContactsLoading: false,
  setFetchContactsLoading: action((state, isLoading) => {
    state.fetchContactsLoading = isLoading;
  }),
  fetchContacts: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchContactsLoading(true);
      const { fulfillmentContextService } = injections;
      const response = await fulfillmentContextService.fetchContacts();
      actions.setContacts(response);
      actions.setFetchContactsLoading(false);
      return response;
    } catch (error) {
      actions.fetchContactsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      actions.setFetchContactsLoading(false);
      return error;
    }
  }),
  fetchContactsFailure: action((state, error) => {
    console.error(error);
  }),
  createContact: thunk(async (actions, contactCreate, { injections }) => {
    try {
      const { assetService } = injections;
      const response = await assetService.createContact(contactCreate);
      actions.setContact(response);
      return response;
    } catch (error) {
      actions.createContactFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'Failed to create contact',
      });
      throw error;
    }
  }),
  updateContact: thunk(async (actions, { contactId, payload }, { injections }) => {
    try {
      const { assetService } = injections;
      const response = await assetService.updateContact({ contactId, payload });
      actions.setContact(response);
      return response;
    } catch (error) {
      actions.updateContactFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message || 'Failed to update contact',
      });
      throw error;
    }
  }),
  createContactFailure: action((state, error) => {
    console.error(error);
  }),
  updateContactFailure: action((state, error) => {
    console.error(error);
  }),
};

export default contactsModel;
